import * as React from "react";
import Table from "@mui/material/Table";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TextField from "@mui/material/TextField";
import TableHead from "@mui/material/TableHead";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import { useQuery, useMutation } from "@apollo/client";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import CircularProgress from "@mui/material/CircularProgress";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";

import DeleteDialog from "./DeleteDialog";
import arraysEqual from "../common/arrays-equal";
import EmployeeTableRow from "./EmployeeTableRow";
import { GET_WAREHOUSES } from "../graphql/warehouses";
import EmployeeEditableTableRow from "./EmployeeEditableTableRow";
import {
	columns,
	emptyUser,
	userToWarehouseAccessType,
	userRolesTranslationConformity,
} from "./common";
import {
	GET_USERS,
	UPDATE_USER,
	DELETE_USER,
	UPDATE_READ_WAREHOUSES,
	UPDATE_WRITE_WAREHOUSES,
} from "../graphql/users";
import ResetPasswordDialog from "./ResetPasswordDialog";
import { CONNECT_ORDERS_TO_MANAGER } from "../graphql/orders";

const prepareGetUsersVariables = ({ search, role }) => {
	return {
		input: {
			search: search ? search : "",
			roles: role ? [role] : [],
		},
	};
};

export default function EmployeePage() {
	const [role, setRole] = React.useState("");
	const [search, setSearch] = React.useState("");

	const { loading, error, data, refetch } = useQuery(GET_USERS, {
		variables: prepareGetUsersVariables({
			search,
			role,
		}),
	});
	const {
		loading: getWarehousesLoading,
		error: getWarehousesError,
		data: getWarehousesData,
	} = useQuery(GET_WAREHOUSES);
	const [
		updateUser,
		{
			loading: updateUserLoading,
			error: updateUserError,
			data: updateUserData,
		},
	] = useMutation(UPDATE_USER);

	// const [
	// 	resetPassword,
	// 	{
	// 		loading: resetPasswordLoading,
	// 		error: resetPasswordError,
	// 		data: resetPasswordData,
	// 	},
	// ] = useMutation(RESET_PASSWORD);

	const [
		deleteUser,
		{
			loading: deleteUserLoading,
			error: deleteUserError,
			data: deleteUserData,
		},
	] = useMutation(DELETE_USER);

	const [
		updateReadWarehouses,
		{
			loading: updateReadWarehousesLoading,
			error: updateReadWarehousesError,
			data: updateReadWarehousesData,
		},
	] = useMutation(UPDATE_READ_WAREHOUSES);
	const [
		updateWriteWarehouses,
		{
			loading: updateWriteWarehousesLoading,
			error: updateWriteWarehousesError,
			data: updateWriteWarehousesData,
		},
	] = useMutation(UPDATE_WRITE_WAREHOUSES);
	const [
		connectOrdersToManager,
		{
			loading: connectOrdersToManagerLoading,
			error: connectOrdersToManagerError,
			data: connectOrdersToManagerData,
		},
	] = useMutation(CONNECT_ORDERS_TO_MANAGER);

	const refetchUsers = async () =>
		refetch(
			prepareGetUsersVariables({
				search,
				role,
			})
		);

	React.useEffect(() => {
		refetchUsers();
	}, [search, role]);

	const [addingNewUser, setAddingNewUser] = React.useState(false);
	const [editableRowId, setEditableRowId] = React.useState(null);
	const isEditableRow = (id) => {
		return editableRowId === id;
	};

	const startEditingRow = (id) => {
		if (!id || editableRowId) return;
		setEditableRowId(() => id);
	};
	const cancelEditingRow = (id) => {
		if (id !== editableRowId) return;
		if (id === emptyUser.id) setAddingNewUser(false);
		setEditableRowId(null);
	};

	const handleUserWarehousesSaving = async ({
		userId,
		userToWarehouses,
		actualRead,
		actualWrite,
	}) => {
		const startRead = userToWarehouses
			? userToWarehouses
					.filter((utw) => utw.accessType === userToWarehouseAccessType.READ)
					.map((utw) => utw.warehouseId)
			: [];
		const startWrite = userToWarehouses
			? userToWarehouses
					.filter((utw) => utw.accessType === userToWarehouseAccessType.WRITE)
					.map((utw) => utw.warehouseId)
			: [];
		if (!arraysEqual(startRead.sort(), actualRead.sort())) {
			await updateReadWarehouses({
				variables: {
					input: {
						userId,
						warehouseIds: actualRead,
					},
				},
			});
		}
		if (!arraysEqual(startWrite.sort(), actualWrite.sort())) {
			await updateWriteWarehouses({
				variables: {
					input: {
						userId,
						warehouseIds: actualWrite,
					},
				},
			});
		}
	};
	const saveRow = async (userData, { readWarehouses, writeWarehouses }) => {
		const isEmptyUser = userData.id === emptyUser.id;
		if (editableRowId !== userData.id) return;
		const updateUserResult = await updateUser({
			variables: {
				input: { ...userData, id: isEmptyUser ? "" : userData.id },
			},
		});

		await handleUserWarehousesSaving({
			userId: isEmptyUser ? updateUserResult.data.updateUser.id : userData.id,
			userToWarehouses: isEmptyUser
				? []
				: data.users.users.find((x) => x.id === userData.id).userToWarehouses,
			actualRead: readWarehouses,
			actualWrite: writeWarehouses,
		});

		await refetchUsers();
		setEditableRowId(null);
		if (isEmptyUser) {
			setAddingNewUser(false);
		}
	};
	const [deleteRowId, setDeleteRowId] = React.useState(null);
	const tryDeleteRow = (id) => {
		if (deleteRowId) return;
		setDeleteRowId(id);
	};
	const deleteRow = async () => {
		await deleteUser({
			variables: {
				id: deleteRowId,
			},
		});
		setDeleteRowId(null);
		setEditableRowId(null);
		await refetchUsers();
	};

	const synchronizeOrdersWithManager = async (id) => {
		await connectOrdersToManager({
			variables: {
				managerUserId: id,
			},
		});
	};

	const [resetPassUser, setResetPassUser] = React.useState(null);
	const tryResetPassword = (user) => {
		if (resetPassUser) return;
		setResetPassUser(user);
	};
	const resetPasswordRow = async () => {
		setResetPassUser(null);
		const auth = getAuth();
		await sendPasswordResetEmail(auth, resetPassUser.email);
		// await resetPassword({
		// 	variables: {
		// 		id: resetPassUser,
		// 	},
		// });
	};

	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(25);

	const isLoading = () =>
		loading ||
		updateUserLoading ||
		deleteUserLoading ||
		getWarehousesLoading ||
		updateReadWarehousesLoading ||
		updateWriteWarehousesLoading ||
		connectOrdersToManagerLoading;

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};
	return (
		<>
			{true && (
				<div>
					<Typography
						variant="h4"
						align="center"
						component="div"
						href="/"
						sx={{
							fontFamily: "monospace",
							fontWeight: 700,
							margin: "auto",
						}}
					>
						Список співробітників
					</Typography>
					<div
						style={{
							display: "flex",
							marginLeft: 10,
							alignItems: "center",
							gap: 15,
							// justifyContent: "flex-end",
						}}
					>
						<div>
							<TextField
								id="outlined-basic"
								label="Пошук за ПІБ"
								variant="outlined"
								sx={{ width: 300 }}
								value={search}
								onChange={(event) => setSearch(event.target.value)}
							/>
						</div>
						<div style={{ minWidth: 120 }}>
							<FormControl fullWidth>
								<InputLabel id="demo-simple-select-label">Роль</InputLabel>
								<Select
									labelId="demo-simple-select-label"
									id="status-select"
									value={role}
									label="Роль"
									onChange={(event) => setRole(event.target.value)}
								>
									<MenuItem key="emptyitem" value="">
										ВСІ
									</MenuItem>
									{Object.keys(userRolesTranslationConformity).map((s) => (
										<MenuItem key={s} value={s}>
											{userRolesTranslationConformity[s]}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</div>
						<div>
							<Button
								onClick={() => {
									setAddingNewUser((oldState) => !oldState);
									setEditableRowId(emptyUser.id);
								}}
								color={addingNewUser ? "secondary" : "primary"}
								variant="contained"
							>
								Створити нового
							</Button>
						</div>
					</div>
					<Paper sx={{ width: "100%", overflow: "hidden" }}>
						{!isLoading() && (
							<>
								<TableContainer sx={{}}>
									<Table stickyHeader aria-label="sticky table">
										<TableHead>
											<TableRow>
												{columns.map((column) => (
													<TableCell
														key={column.id}
														align={column.align}
														style={{ minWidth: column.minWidth }}
													>
														{column.label}
													</TableCell>
												))}
											</TableRow>
										</TableHead>
										<TableBody>
											{addingNewUser && (
												<EmployeeEditableTableRow
													key={emptyUser.id}
													row={emptyUser}
													warehouses={getWarehousesData.warehouses}
													actions={{
														saveRow: saveRow,
														cancelEdit: cancelEditingRow,
													}}
												/>
											)}
											{data.users.users
												.slice(
													page * rowsPerPage,
													page * rowsPerPage + rowsPerPage
												)
												.map((user) =>
													isEditableRow(user.id) ? (
														<EmployeeEditableTableRow
															key={user.id}
															row={user}
															warehouses={getWarehousesData.warehouses}
															actions={{
																saveRow: saveRow,
																tryDeleteRow: tryDeleteRow,
																cancelEdit: cancelEditingRow,
															}}
														/>
													) : (
														<EmployeeTableRow
															key={user.id}
															row={user}
															buttonsActive={!editableRowId}
															actions={{
																editRow: startEditingRow,
																tryDeleteRow: tryDeleteRow,
																synchronizeOrdersWithManager:
																	synchronizeOrdersWithManager,
																tryResetPassword: tryResetPassword,
															}}
														/>
													)
												)}
										</TableBody>
									</Table>
								</TableContainer>
								<TablePagination
									rowsPerPageOptions={[10, 25, 100]}
									component="div"
									count={data.users.users.length}
									rowsPerPage={rowsPerPage}
									page={page}
									onPageChange={handleChangePage}
									onRowsPerPageChange={handleChangeRowsPerPage}
								/>
							</>
						)}
					</Paper>
				</div>
			)}
			{isLoading() && (
				<CircularProgress
					size={24}
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						// marginTop: "-12px",
						// marginLeft: "-12px",
					}}
				/>
			)}
			<DeleteDialog
				open={!!deleteRowId}
				cancelAction={() => setDeleteRowId(null)}
				confirmAction={deleteRow}
			/>
			<ResetPasswordDialog
				open={!!resetPassUser}
				cancelAction={() => setResetPassUser(null)}
				confirmAction={resetPasswordRow}
			/>
			{/* {showUpdateAlert && (
				<Alert
					severity="error"
					sx={{
						// height: 20,
						position: "absolute",
						// top: "100%",
						// left: "50%",
						// marginTop: "-30px",
						// marginLeft: "-12px",
						right: 2,
						bottom: 2,
					}}
				>
					{updateUserError.message}
				</Alert>
			)} */}
		</>
	);
}
